import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/brand";

export default function AdminLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome back!");
      nav("/admin");
    } catch (err) {
      const msg = err.response?.data?.detail || "Unable to sign in";
      toast.error(typeof msg === "string" ? msg : "Invalid credentials");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-160px)] grid grid-cols-1 lg:grid-cols-2" data-testid="admin-login-page">
      <div className="hidden lg:block relative overflow-hidden">
        <img src="https://images.pexels.com/photos/36324985/pexels-photo-36324985.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-sj-ink/70 via-sj-ink/30 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="overline text-sj-gold">Owner console</div>
          <h2 className="font-display text-4xl font-light mt-3 leading-tight">
            Manage your boutique with a single sign-in.
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-14">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="admin-login-form">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} className="h-12 w-12 object-contain shimmer" alt="" />
            <div>
              <div className="font-display font-medium tracking-wide">STAR JEWELS</div>
              <div className="overline text-[10px]">Owner panel</div>
            </div>
          </div>

          <h1 className="font-display text-3xl font-light mt-10">Welcome back.</h1>
          <p className="text-sj-muted text-sm mt-1">Sign in to manage products & orders.</p>

          <div className="mt-8 space-y-4">
            <div>
              <label className="text-xs text-sj-inkSoft">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm"
                data-testid="admin-email"
              />
            </div>
            <div>
              <label className="text-xs text-sj-inkSoft">Password</label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required
                  className="mt-1.5 w-full bg-white border border-sj-ink/15 focus:border-sj-goldDark outline-none rounded-md px-4 h-11 text-sm pr-11"
                  data-testid="admin-password"
                />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-4 text-sj-muted" data-testid="admin-toggle-password">
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          </div>

          <button type="submit" disabled={busy} className="btn-primary w-full justify-center mt-8 disabled:opacity-60" data-testid="admin-submit">
            <Lock size={16} /> {busy ? "Signing in…" : "Sign in"}
          </button>

          <div className="mt-6 text-center">
            <Link to="/" className="text-xs text-sj-muted hover:text-sj-goldDark" data-testid="admin-back-home">← Back to store</Link>
          </div>
        </form>
      </div>
    </main>
  );
}
